import { lazy, type ReactNode, Suspense, useMemo } from 'react'
import {
  Tab as AriaTab,
  TabList as AriaTabList,
  TabPanel as AriaTabPanel,
  Tabs as AriaTabs,
  type Key,
} from 'react-aria-components'
import Text from 'src/components/ui/Text'
import { twMerge as cn } from 'tailwind-merge'
import { Preloader } from '../Preloader/Preloader'

const UserCouponView = lazy(
  () => import('src/components/UserProfile/views/UserCouponView'),
)
const BenefitCardList = lazy(() =>
  import('src/features/Kaisuuken/components/BenefitCardList').then(m => ({
    default: m.BenefitCardList,
  })),
)
const BenefitCardConsumeLog = lazy(() =>
  import('src/features/Kaisuuken/components/BenefitCardConsumeLog').then(m => ({
    default: m.BenefitCardConsumeLog,
  })),
)

type TabItem = {
  value: Key
  label: ReactNode
}

export default function UserAssetsTab(props: {
  userId: string
  currentTab: string
  isCouponSystemEnabled: boolean
  onChange: (value: string) => void
}) {
  const tabs = useMemo<TabItem[]>(() => {
    const items: TabItem[] = []
    if (props.isCouponSystemEnabled) {
      items.push({
        value: 'coupons',
        label: <Text i18nKey="user:profile-sub-tab.coupon" />,
      })
    }
    items.push({
      value: 'benefit-cards',
      label: <Text i18nKey="user:profile-sub-tab.benefit-cards" />,
    })
    return items
  }, [props.isCouponSystemEnabled])

  return (
    <div className="pb-12 space-y-4">
      <AriaTabs
        selectedKey={props.currentTab}
        onSelectionChange={value => props.onChange(value as string)}
      >
        <AriaTabList items={tabs} className={cn('flex flex-wrap gap-1 mb-4')}>
          {item => (
            <AriaTab
              id={item.value}
              className={cn(
                'px-4 py-2 rounded-full outline-none cursor-default',
                'hover:bg-black/5 dark:hover:bg-white/10',
                'selected:bg-black/10 dark:selected:text-neutral-100 dark:selected:bg-white/20',
                'focus-visible:outline focus-visible:outline-offset-1 focus-visible:outline-blue-500',
              )}
            >
              {item.label}
            </AriaTab>
          )}
        </AriaTabList>
        {props.isCouponSystemEnabled && (
          <AriaTabPanel id="coupons" className="contents">
            <Suspense fallback={<Preloader />}>
              <UserCouponView userId={props.userId} />
            </Suspense>
          </AriaTabPanel>
        )}
        <AriaTabPanel id="benefit-cards" className="contents">
          <div className="flex flex-col gap-12">
            <Suspense fallback={<Preloader />}>
              <BenefitCardList />
            </Suspense>
            <Suspense fallback={<Preloader />}>
              <BenefitCardConsumeLog />
            </Suspense>
          </div>
        </AriaTabPanel>
      </AriaTabs>
    </div>
  )
}
